import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Clock, Gift, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import CountdownTimer from "@/components/CountdownTimer";

import { API } from "@/lib/api";

const BOOK_COVER = "/images/ebook-cover.jpg";

export default function ExitIntentPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { email, lead_type: "chapter" });
      navigate("/thank-you?type=chapter");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#050505] min-h-screen flex items-center justify-center px-6 py-16" data-testid="exit-intent-page">
      <div className="max-w-2xl w-full space-y-10 text-center">
        {/* Urgency Header */}
        <div className="space-y-4 animate-fade-in">
          <div className="inline-flex items-center gap-2 bg-red-600/10 border border-red-600/30 px-4 py-2 rounded-sm">
            <Clock className="w-4 h-4 text-red-500" />
            <span className="text-red-400 text-xs tracking-[0.15em] uppercase font-bold">Don't Miss This</span>
          </div>
          <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter uppercase text-white">
            Before You Go...
          </h1>
        </div>

        {/* Book + Offer */}
        <div className="bg-[#121212] border border-[#D4AF37]/30 p-8 sm:p-12 rounded-sm space-y-8 opacity-0 animate-fade-in-up stagger-2">
          <div className="flex flex-col sm:flex-row items-center gap-8">
            <img
              src={BOOK_COVER}
              alt="The Forty Year Bender by Chris Merlo - Find Your Breakthrough FYB - CLARITY Protocol - Free Chapter"
              title="The Forty Year Bender by Chris Merlo - Get Your Free Chapter"
              className="w-40 shadow-xl shadow-black/50 flex-shrink-0"
              data-testid="exit-book-cover"
            />
            <div className="text-left space-y-3">
              <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">Free — No Catch</p>
              <h2 className="font-heading text-3xl tracking-tight uppercase text-white">
                Get The First Chapter Free
              </h2>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Read the prologue of The Forty Year Bender. Seven rehabs. Two overdoses.
                Forty years of chaos. And the moment everything changed. This chapter alone will
                show you why this book exists.
              </p>
            </div>
          </div>

          {/* Email Form */}
          <div className="space-y-4 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
              data-testid="exit-page-email-input"
            />
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full btn-gold h-14 rounded-sm text-sm tracking-[0.15em] uppercase animate-pulse-gold"
              data-testid="exit-page-submit-button"
            >
              {loading ? "Sending..." : "Send Me The Free Chapter"}
            </Button>
            <p className="text-zinc-600 text-xs text-center">No spam. No tricks. Just one powerful chapter.</p>
          </div>

          <div className="gold-line" />

          {/* Bonus Urgency */}
          <div className="space-y-3 text-center">
            <div className="flex items-center justify-center gap-2 text-[#D4AF37]">
              <Gift className="w-4 h-4" />
              <span className="text-sm font-semibold">Pre-Order The Book</span>
            </div>
            <div className="flex justify-center">
              <CountdownTimer />
            </div>
            <p className="text-zinc-500 text-xs">
              Pre-order at <span className="text-[#D4AF37]">$29.99 AUD</span> + shipping
            </p>
          </div>
        </div>

        {/* Dismiss */}
        <Link
          to="/"
          className="inline-block text-zinc-600 text-xs hover:text-zinc-400 transition-colors"
          data-testid="exit-back-home"
        >
          No thanks, take me back to the homepage
        </Link>
      </div>
    </div>
  );
}
